import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { ROUTES } from '@/lib/constants';
import { isAdmin } from '@/types/auth';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import { SignOutDialog } from '@/components/shared/SignOutDialog';
import {
  LayoutDashboard,
  GraduationCap,
  BarChart3,
  Users,
  Building2,
  BookOpen,
  FileText,
  FileCheck,
  ClipboardList,
  Settings,
  LogOut,
  User,
  ShieldCheck,
} from 'lucide-react';

const employeeNavItems = [
  { title: 'Dashboard', url: ROUTES.DASHBOARD, icon: LayoutDashboard },
  { title: 'Training', url: '/training', icon: GraduationCap },
  { title: 'My Progress', url: '/progress', icon: BarChart3 },
  { title: 'My Records', url: ROUTES.MY_RECORDS, icon: FileCheck },
  { title: 'Policies', url: ROUTES.POLICIES, icon: FileText },
  { title: 'Profile', url: ROUTES.PROFILE, icon: User },
];

const adminNavItems = [
  { title: 'Dashboard', url: ROUTES.ADMIN_DASHBOARD, icon: LayoutDashboard },
  { title: 'Users', url: ROUTES.ADMIN_USERS, icon: Users },
  { title: 'Departments', url: ROUTES.ADMIN_DEPARTMENTS, icon: Building2 },
  { title: 'Modules', url: ROUTES.ADMIN_MODULES, icon: BookOpen },
  { title: 'Policies', url: ROUTES.ADMIN_POLICIES, icon: FileText },
  { title: 'Records', url: ROUTES.ADMIN_RECORDS, icon: ClipboardList },
  { title: 'Compliance', url: ROUTES.ADMIN_COMPLIANCE, icon: ShieldCheck },
  { title: 'Compliance Settings', url: ROUTES.ADMIN_COMPLIANCE_SETTINGS, icon: Settings },
];

export function AppSidebar() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  const handleSignOut = async () => {
    await signOut();
    navigate(ROUTES.HOME);
  };

  const isActive = (path: string) => {
    if (path === ROUTES.DASHBOARD) {
      return location.pathname === path;
    }
    if (path === ROUTES.ADMIN_DASHBOARD) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };
  const showAdminSection = user && isAdmin(user.roles);

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="border-b border-sidebar-border p-4">
        <Link to={ROUTES.DASHBOARD} className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading font-bold">
            D
          </div>
          {!isCollapsed && (
            <span className="font-heading text-lg font-semibold text-sidebar-foreground">
              Ditan
            </span>
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Main Menu</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {employeeNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.url)}
                    tooltip={item.title}
                  >
                    <Link to={item.url}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {showAdminSection && (
          <SidebarGroup>
            <SidebarGroupLabel>Administration</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminNavItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      tooltip={item.title}
                    >
                      <Link to={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Settings">
              <Link to="/settings">
                <Settings className="h-4 w-4" />
                <span>Settings</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SignOutDialog
              onSignOut={handleSignOut}
              trigger={
                <SidebarMenuButton
                  tooltip="Sign Out"
                  className="text-destructive hover:text-destructive"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </SidebarMenuButton>
              }
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
